import { GlobalSettings } from "../models/GlobalSettings";
import { PagePreference } from "../models/PagePreference";
import { UserPreferences } from "../models/UserPreferences";

export const USER_PREFERENCES_KEY = "user_preferences";
export const USER_PREFERENCES_LAST_SAVED_KEY = "user_preferences_last_saved";

/**
 * Utility for managing user preferences stored in sessionStorage.
 * 
 * Handles saving, updating, retrieving, and clearing page-specific grid preferences.
 * Preferences include grid layout, theme, visibility, and view configurations.
 */
export const userPreferencesStorage = {
    /**
    * Retrieves all user preferences from session storage.
    * 
    * @returns The full UserPreferences object, or null if not found.
    */
    getAll(): UserPreferences | null {
        const data = sessionStorage.getItem(USER_PREFERENCES_KEY);
        return data ? JSON.parse(data) : null;
    },

    /**
    * Retrieves a specific page's preferences by key.
    * 
    * @param pageKey The unique key identifying the page (e.g., "transactions").
    * @returns The PagePreference object for the specified page, or null if not found.
    */
    getPagePreference(pageKey: string): PagePreference | null {
        const all = this.getAll();
        return all?.preferences?.[pageKey] ?? null;
    },

    /**
    * Get global application settings.
    * @returns GlobalSettings or null if not set.
    */
    getGlobalSettings(): GlobalSettings | null {
        const all = this.getAll();
        return all?.globalSettings ?? null;
    },

    /**
    * Sets or replaces a specific page's preference.
    * Automatically updates the `updatedAt` timestamp within the page meta.
    * 
    * @param pageKey The unique key identifying the page.
    * @param pref The full PagePreference object to store.
    */
    setPagePreference(pageKey: string, pref: PagePreference) {
        const all = this.getAll() ?? { preferences: {} };
        all.preferences[pageKey] = {
            ...pref,
            pageMeta: {
                ...pref.pageMeta,
                updatedAt: new Date().toISOString(),
            },
        };
        sessionStorage.setItem(USER_PREFERENCES_KEY, JSON.stringify(all));
    },

    /**
    * Updates one or more properties of a page's preference.
    * 
    * Merges the existing settings with the provided partial updates.
    * Ensures `updatedAt` timestamp is refreshed automatically.
    * 
    * @param pageKey The unique key identifying the page.
    * @param partial A partial PagePreference object containing fields to update.
    */
    updatePagePreference(pageKey: string, partial: Partial<PagePreference>) {
        const existing: PagePreference = this.getPagePreference(pageKey);
        const updated: PagePreference = {
            gridSettings: {
                hiddenColumns:
                    partial.gridSettings?.hiddenColumns ??
                    existing?.gridSettings.hiddenColumns ??
                    [],
                columnOrder:
                    partial.gridSettings?.columnOrder ??
                    existing?.gridSettings.columnOrder ??
                    [],
                density:
                    partial.gridSettings?.density ??
                    existing?.gridSettings.density ??
                    "standard",
                updatedAt: new Date().toISOString(),
                pinnedColumns:
                    partial.gridSettings?.pinnedColumns ??
                    existing?.gridSettings.pinnedColumns ??
                    {},
            },
            viewSettings: {
                theme:
                    partial.viewSettings?.theme ??
                    existing?.viewSettings?.theme ??
                    "light",
                showGridLines:
                    partial.viewSettings?.showGridLines ??
                    existing?.viewSettings?.showGridLines,
                showRowNumbers:
                    partial.viewSettings?.showRowNumbers ??
                    existing?.viewSettings?.showRowNumbers,
            },
            pageMeta: {
                ...existing?.pageMeta,
                ...partial.pageMeta,
                updatedAt: new Date().toISOString(),
            },
        };
        this.setPagePreference(pageKey, updated);
    },

    /**
    * Set global application settings.
    * @param settings GlobalSettings object
    */
    setGlobalSettings(settings: GlobalSettings) {
        const all = this.getAll() ?? { preferences: {} };
        all.globalSettings = settings;
        sessionStorage.setItem(
            USER_PREFERENCES_KEY, 
            JSON.stringify(all)
        );
    },

    /**
    * Update specific parts of global settings without overwriting the entire object.
    * @param partial Partial GlobalSettings object
    */
    updateGlobalSettings(partial: Partial<GlobalSettings>) {
        const existing = this.getGlobalSettings() ?? {};
        const updated = { ...existing, ...partial };
        this.setGlobalSettings(updated);
    },

    /**
    * @returns The a boolean value to decide if the user preference is changed.
    */
    isUserPreferenceChanged(): boolean{
        var currentPreference = sessionStorage.getItem(USER_PREFERENCES_KEY);
        var previousPreference = sessionStorage.getItem(USER_PREFERENCES_LAST_SAVED_KEY);

        return currentPreference !== previousPreference;
    },

    /**
    * Sets all user preferences at once from a JSON string.
    * Typically used for initial load or synchronization from backend.
    * 
    * @param UserPreferenceJson The full serialized UserPreferences JSON string.
    */
    setAll(UserPreferenceJson: string) {
        sessionStorage.setItem(
            USER_PREFERENCES_KEY, 
            UserPreferenceJson
        );
        sessionStorage.setItem(
            USER_PREFERENCES_LAST_SAVED_KEY,
            UserPreferenceJson
        );
    },

    /**
    * Clears all stored user preferences from session storage.
    * Useful when logging out or resetting state.
    */
    clear() {
        sessionStorage.removeItem(USER_PREFERENCES_KEY);
        sessionStorage.removeItem(USER_PREFERENCES_LAST_SAVED_KEY);
    },
};

/**
* Converts a hiddenColumns array into a MUI columnVisibilityModel object.
* 
* Example:
* ```ts
* toHiddenColumnRecord(["name", "status"])
* // => { name: false, status: false }
* ```
* 
* @param hiddenColumns The array of hidden column field names.
* @returns A record mapping hidden column fields to `false` (invisible).
*/
export const toHiddenColumnRecord = (
    hiddenColumns: string[] = []
): Record<string, boolean> =>
    hiddenColumns.reduce<Record<string, boolean>>((acc, col) => {
        acc[col] = false;
        return acc;
    }, {});

/**
* Converts a MUI columnVisibilityModel object back to a hiddenColumns array.
* 
* Example:
* ```ts
* fromColumnVisibilityModel({ name: false, age: true })
* // => ["name"]
* ```
* 
* @param model The visibility model where each key is a column field.
* @returns An array of field names that are currently hidden.
*/
export const fromColumnVisibilityModel = (
    model: Record<string, boolean>
): string[] => {
    return Object.entries(model)
        .filter(([_, visible]) => !visible)
        .map(([field]) => field);
};
